import { Component, Input, OnInit } from '@angular/core';
import { FiltersService } from 'src/app/core/services/filters.service';
import { ServiceInsightsService } from '../../../services/service-insights-service.service';
import {
  ServiceInsightRecord,
  ServiceInsightsCallData,
  ServiceInsightsObservationsData,
  ServiceMetricTypes,
  SICallTypes,
  SICallTypeToServiceMetricType,
} from '../../../types/service-insight-types';

@Component({
    selector: 'service-details-metric-card',
    templateUrl: './service-details-metric-card.component.html',
    styleUrls: ['./service-details-metric-card.component.scss'],
    standalone: false
})
export class ServiceDetailsMetricCardComponent implements OnInit {
  @Input() metricName: string = '';
  @Input() callType: SICallTypes = SICallTypes.UntrackedCalls;

  serviceMetricType: ServiceMetricTypes = ServiceMetricTypes.Calls;
  totalCount: number = 0;
  percentGrowth: number = 0;
  growthDateRangeAvailable: boolean = false;
  displayLoader: boolean = false;

  constructor(
    private _filterService: FiltersService,
    private _serviceInsightsService: ServiceInsightsService,
  ) {}

  async ngOnInit() {
    this.serviceMetricType = SICallTypeToServiceMetricType[this.callType];
    this.subscribeToServiceMetricData();
    this.growthDateRangeAvailable =
      this._filterService.IsPreviousPeriodAvailable();
    await (this.serviceMetricType === ServiceMetricTypes.Calls
      ? this.fetchCallsData()
      : this.fetchObsData());
  }

  private subscribeToServiceMetricData(): void {
    if (this.serviceMetricType === ServiceMetricTypes.Calls) {
      this._serviceInsightsService
        .getServiceInsightsCallData(
          this.callType as keyof ServiceInsightsCallData,
        )
        .subscribe((d) => {
          this.totalCount = d.periods.reduce(
            (sum: number, r: ServiceInsightRecord) => sum + r.total,
            0,
          );
          this.percentGrowth = Math.round(d.percentChangeInCount);
        });
    } else {
      this._serviceInsightsService
        .getServiceInsightsObsData(this.callType as keyof ServiceInsightsObservationsData)
        .subscribe((d) => {
          this.totalCount = d.periods.reduce(
            (sum: number, r: ServiceInsightRecord) => sum + r.total,
            0,
          );
          this.percentGrowth = Math.round(d.percentChangeInCount);
        });
    }
  }

  /**
   * Fetches the calls data from the API if it is not already available in the service.
   * Ideally, this should never be called since drilldown page should be loaded after the data is fetched in the parent component.
   * However, this is a fallback to ensure that the data is available when the component is initialized.
   * @returns A promise that resolves when the calls data has been fetched.
   */
  private async fetchCallsData(): Promise<void> {
    const hasData =
      this._serviceInsightsService
        .getServiceInsightsCallData(
          this.callType as keyof ServiceInsightsCallData,
        )
        .getValue().periods.length > 0;

    if (hasData) return;

    this.displayLoader = true;
    const response =
      await this._serviceInsightsService.getSIMetricByPeriodFromAPI(
        this.serviceMetricType,
        this.callType,
        {
          accountNumber: [],
          accountName: [],
          country: [],
          state: [],
          city: [],
          zipCode: [],
          recurringIssue: [],
        },
      );
    this._serviceInsightsService.setServiceInsightsCallData(
      this.callType as keyof ServiceInsightsCallData,
      response,
    );
    this.displayLoader = false;
  }

  /**
   * Fetches the observation data from the API if it is not already available in the service.
   * Ideally, this should never be called since drilldown page should be loaded after the data is fetched in the parent component.
   * However, this is a fallback to ensure that the data is available when the component is initialized.
   * @returns A promise that resolves when the observation data has been fetched.
   */
  private async fetchObsData(): Promise<void> {
    const hasData =
      this._serviceInsightsService.getServiceInsightsObsData(this.callType as keyof ServiceInsightsObservationsData).getValue()
        .periods.length > 0;

    if (hasData) return;

    this.displayLoader = true;
    const response =
      await this._serviceInsightsService.getSIMetricByPeriodFromAPI(
        this.serviceMetricType,
        this.callType,
        {
          accountNumber: [],
          accountName: [],
          country: [],
          state: [],
          city: [],
          zipCode: [],
          recurringIssue: [],
        },
      );
    this._serviceInsightsService.setServiceInsightsObsData(this.callType as keyof ServiceInsightsObservationsData, response);
    this.displayLoader = false;
  }
}
